import { SVGAssets } from '@business/assets/vectors';
import React, { useState, useCallback, useRef, useEffect, FC } from 'react';
import { useDropzone } from 'react-dropzone';
import { uploadToS3, validateImageFile } from '@utils/helpers/s3Upload';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getToken } from '@ports/common/sessionStateSlice';
import { useAddUserDetailsMutation } from '@ports/internal-services/userDetails/userDetailsSlice';
import { setAlertDetails } from '@ports/common/AlertStateSlice';
import { routePaths } from '@utils/constants/routePaths';
import { useNavigate } from 'react-router-dom';
import { useGetAssetsQuery } from '@ports/internal-services/asset/assetSlice';
import { Button } from '../Button';
import { ImageCropperModal } from './ImageCropperModal';

interface UploadPhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onUploadSuccess?: () => void; // Add this prop
}

// Extend File to include preview property
type FileWithPreview = File & { preview: string };

export const UploadPhotoModal: FC<UploadPhotoModalProps> = ({
  isOpen,
  onClose,
  userId,
  onUploadSuccess
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector(getToken);
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showCropper, setShowCropper] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [isViewMore, setIsViewMore] = useState<boolean>(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const [UpdateUserDetails] = useAddUserDetailsMutation();

  const [filterOptions, setFilterOptions] = useState<any>({
    limit: 4,
    offset: 0
  });

  const { data: assetData, refetch: assetRefetch } = useGetAssetsQuery(
    filterOptions,
    {
      refetchOnMountOrArgChange: true
    }
  );

  useEffect(() => {
    if (isViewMore) {
      setFilterOptions({});
    } else {
      setFilterOptions({ limit: 4, offset: 0 });
    }
  }, [isViewMore]);

  useEffect(() => {
    assetRefetch();
  }, []);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setError('');
    const file = acceptedFiles[0];

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    // Open cropper with selected image
    const imageUrl = URL.createObjectURL(file);
    setSelectedImageUrl(imageUrl);
    setShowCropper(true);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false
  });

  const handleCropComplete = (croppedFile: File) => {
    // Add cropped file to preview
    const fileWithPreview = Object.assign(croppedFile, {
      preview: URL.createObjectURL(croppedFile)
    }) as FileWithPreview;
    setFiles([fileWithPreview]);
    setShowCropper(false);
    if (selectedImageUrl) {
      URL.revokeObjectURL(selectedImageUrl);
    }
    setSelectedImageUrl('');
  };

  const handleCropperClose = () => {
    setShowCropper(false);
    if (selectedImageUrl) {
      URL.revokeObjectURL(selectedImageUrl);
      setSelectedImageUrl('');
    }
  };

  const handleUploadComplete = async () => {
    // After successful upload
    if (onUploadSuccess) {
      await onUploadSuccess();
    }
    onClose();
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select an image first');
      return;
    }

    if (!userId) {
      setError('User ID not found');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const idToken = token || '';

      if (!idToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      const imageUrl = await uploadToS3(files[0], idToken, userId, 'profile');
      const dataObj = {
        createUserDetails: {
          profileImage: imageUrl
        }
      };
      UpdateUserDetails(dataObj).then((res: any) => {
        if (res?.data) {
          dispatch(
            setAlertDetails({
              alert: {
                type: 'success',
                message: t('applySuccess')
              },
              scope: 'profile'
            })
          );
          navigate(routePaths.MyProfile);
        } else {
          dispatch(
            setAlertDetails({
              alert: {
                type: 'error',
                message: res.error
              },
              scope: 'profile'
            })
          );
        }
      });
      handleUploadComplete();
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoSelect = (photoUrl: string) => {
    setSelectedPhoto(photoUrl);
  };

  const handleUseSelectedPhoto = async () => {
    if (!selectedPhoto) {
      setError('Please select a photo first');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const dataObj = {
        createUserDetails: {
          profileImage: selectedPhoto
        }
      };

      const res: any = await UpdateUserDetails(dataObj);

      if (res?.data) {
        dispatch(
          setAlertDetails({
            alert: {
              type: 'success',
              message: t('applySuccess')
            },
            scope: 'profile'
          })
        );
        navigate(routePaths.MyProfile);
        await handleUploadComplete();
      } else {
        dispatch(
          setAlertDetails({
            alert: {
              type: 'error',
              message: res.error
            },
            scope: 'profile'
          })
        );
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update profile image');
      console.error('Update error:', err);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if cropper is open
      if (showCropper) return;

      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cleanup previews
      files.forEach(file => URL.revokeObjectURL(file.preview));
    };
  }, [isOpen, onClose, files, showCropper]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div
          ref={modalRef}
          className={`bg-white rounded-2xl p-6 shadow-lg relative max-h-[90vh] overflow-y-auto ${
            isViewMore ? 'w-3/4' : 'w-[480px]'
          }`}>
          {/* Close Button */}
          <Button
            btnStyle="link"
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl disabled:opacity-50"
            onClick={onClose}
            disabled={uploading}>
            <SVGAssets.CloseIconGray className="w-3 h-3" />
          </Button>

          {/* Title */}
          <h2 className="text-lg font-semibold text-[#353535] mb-2">
            {t('Choose your profile picture')}
          </h2>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Upload Section */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              isDragActive ? 'border-green-500 bg-green-50' : 'border-gray-300'
            } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            <input {...getInputProps()} disabled={uploading} />
            <div className="flex flex-col items-center justify-center text-[#353535]">
              {files.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="flex items-center justify-center mb-3">
                    <SVGAssets.ModalUploadIcon className="w-14 h-14" />
                  </div>
                  <p className="font-medium">{t('Upload your files here')}</p>
                  <p className="text-sm text-gray-500">
                    {t('Drag an image here or')}
                  </p>
                </div>
              ) : (
                <div className="mt-4 flex justify-center">
                  <div className="flex flex-wrap justify-center gap-3">
                    {files.map(file => (
                      <div key={file.name} className="relative">
                        <img
                          src={file.preview}
                          alt="preview"
                          className="rounded-lg h-24 w-24 object-cover"
                        />
                        <Button
                          btnStyle="link"
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            setFiles([]);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600">
                          <SVGAssets.CloseIconGray className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <SVGAssets.AddRoundIcon className="w-4 h-4" />
                <p className="text-[#7F9D48] font-medium underline mt-1">
                  {t('Browse file to upload')}
                </p>
              </div>
            </div>
          </div>

          {/* Gallery Section */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-[#353535]">
                {t('photosPage.uploadPhotos')}
              </h3>
              {assetData?.data?.count > 0 && (
                <Button
                  btnStyle="link"
                  className="text-sm text-[#7F9D48] font-medium"
                  onClick={() => setIsViewMore(!isViewMore)}>
                  {isViewMore ? t('viewLess') : t('viewMore')}
                </Button>
              )}
            </div>

            {assetData?.data?.rows && assetData.data.rows.length > 0 ? (
              <div
                className={`grid gap-2 grid-cols-[repeat(auto-fill,minmax(90px,1fr))]`}>
                {assetData.data.rows.map((photo: any, index: number) => (
                  <div
                    key={photo.id || index}
                    onClick={() => handlePhotoSelect(photo.url)}
                    className={`relative cursor-pointer rounded-lg overflow-hidden w-20 h-20 border-2 transition-all ${
                      selectedPhoto === photo.url
                        ? 'border-[#7F9D48] shadow-md'
                        : 'border-transparent hover:border-gray-300'
                    }`}>
                    <img
                      src={photo.url || '/placeholder-image.jpg'}
                      alt={photo.name || `Photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />

                    {selectedPhoto === photo.url && (
                      <div className="absolute inset-0 bg-[#7F9D48]/40 flex items-center justify-center">
                        {/* <div className="w-6 h-6 bg-[#7F9D48] rounded-full flex items-center justify-center">
                            <SVGAssets.TickIcon className="w-4 h-4 text-[#7F9D48]" />
                          </div> */}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500 text-sm">
                {t('photosPage.noPhotosYet')}
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-between mt-6">
            <Button
              onClick={onClose}
              disabled={uploading}
              className="cancel-btn">
              {t('cancel')}
            </Button>
            <Button
              btnStyle="primary"
              onClick={selectedPhoto ? handleUseSelectedPhoto : handleUpload}
              disabled={uploading || (files.length === 0 && !selectedPhoto)}
              className="bg-[#7F9D48] text-white font-medium px-5 py-2 rounded-md hover:bg-[#6d8c3e] transition disabled:opacity-50 disabled:cursor-not-allowed">
              {uploading
                ? t('dashboard.uploading')
                : t('Save as profile picture')}
            </Button>
          </div>
        </div>
      </div>

      {/* Image Cropper Modal */}
      {showCropper && (
        <ImageCropperModal
          isOpen={showCropper}
          imageUrl={selectedImageUrl}
          onClose={handleCropperClose}
          onCropComplete={handleCropComplete}
        />
      )}
    </>
  );
};
